import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight, ShieldCheck, TrendingUp, Building2, TrainFront, Quote,
  LineChart, FileText, Landmark,
} from "lucide-react";
import { Reveal, MaskLine, CountUp, ASSETS } from "@/lib/motion";
import Nav from "@/components/bwg/Nav";
import Footer from "@/components/bwg/Footer";
import WhatsAppFab from "@/components/bwg/WhatsAppFab";
import FaqSection, { FAQS } from "@/components/bwg/FaqSection";
import RociEngine from "@/components/bwg/RociEngine";
import WhereWeOperate from "@/components/bwg/WhereWeOperate";
import Seo from "@/components/bwg/Seo";
import { useBooking } from "@/components/bwg/BookingModal";
import { postToZohoWebToLead } from "@/lib/zoho";
import { orgSchema, faqSchema, breadcrumbSchema } from "@/lib/schema";

/* ------------------------------ HERO ------------------------------ */
function Hero() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 160]);
  const overlayY = useTransform(scrollY, [0, 700], [0, 60]);
  const { open: openBooking } = useBooking();
  return (
    <header id="top" data-testid="hero-section" className="relative min-h-screen overflow-hidden bg-brand-dark">
      <motion.div style={{ y }} className="absolute inset-0 -z-0">
        <img src={ASSETS.hero} alt="Aerial view of an Indian expressway corridor cutting through farmland at sunset"
          className="w-full h-[115%] object-cover animate-ken-burns" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-dark via-brand-dark/50 to-brand-dark/70" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-dark/80 via-transparent to-transparent" />
      </motion.div>
      <div className="grain" />
      <motion.div style={{ y: overlayY }} className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 min-h-screen flex flex-col justify-end pb-20 pt-32">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="flex items-center gap-3 mb-8">
          <span className="h-px w-10 bg-brand-orange" />
          <span className="text-brand-offwhite/70 text-xs tracking-[0.25em] uppercase font-medium">Land Investment Advisory · Connaught Place, Delhi</span>
        </motion.div>
        <h1 data-testid="hero-headline" className="font-display font-semibold text-brand-offwhite text-5xl sm:text-6xl lg:text-8xl tracking-tighter leading-[0.95] max-w-5xl">
          <MaskLine delay={0.3}>Land is the</MaskLine>
          <MaskLine delay={0.42}><span className="text-brand-orange">timeless formula</span></MaskLine>
          <MaskLine delay={0.54}>for wealth creation.</MaskLine>
        </h1>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.8 }} className="mt-10 flex flex-wrap items-center gap-4">
          <button type="button" onClick={() => openBooking("default", "hero_modal")} data-testid="hero-cta-button" className="group inline-flex items-center gap-2 rounded-full bg-brand-orange px-7 py-4 text-base font-semibold text-white hover:bg-brand-offwhite hover:text-brand-ink transition-colors duration-300">
            Book a free 15-minute call
            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
          </button>
          <a href="#track" className="text-brand-offwhite/70 text-sm hover:text-brand-offwhite transition-colors underline underline-offset-4">See our verified track record</a>
        </motion.div>
      </motion.div>
    </header>
  );
}

function Transition() {
  return (
    <section className="bg-brand-paper py-20 lg:py-28" id="about">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <Reveal>
          <p className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-brand-ink max-w-4xl mx-auto text-center">
            India is about to build more infrastructure in the next decade than in the seventy years before it. <span className="text-brand-orange">Very few people will own the land it runs through.</span>
          </p>
        </Reveal>
      </div>
    </section>
  );
}

const PILLARS = [
  { icon: LineChart, img: ASSETS.dataTxn, tag: "Signal 01", title: "Live Transactions", body: "We read price momentum directly from registered sale deeds at the sub-registrar. These are real, recorded transactions, not asking prices." },
  { icon: FileText, img: ASSETS.dataTender, tag: "Signal 02", title: "Live Tenders", body: "Government infrastructure tenders are a leading indicator. A sanctioned expressway or metro line moves land value long before it opens." },
  { icon: Landmark, img: ASSETS.cluLand, tag: "Signal 03", title: "CLU-Eligible Land", body: "A zoning and regulatory filter. We isolate land with a legal path from agricultural to commercial or residential use, where upside is permitted, not just hoped for." },
];

function Intelligence() {
  return (
    <section id="intelligence" className="bg-brand-offwhite py-24 lg:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mb-16">
          <span className="text-brand-orange text-sm font-semibold tracking-[0.2em] uppercase">Chapter 01 · Demand-Supply Intelligence</span>
          <h2 className="mt-4 font-display font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] text-brand-ink">New infrastructure creates new corridors. New corridors create a gap between demand and supply. That gap is where prices move, and where we look.</h2>
        </div>
        {/* Primary content: the three signals */}
        <div className="grid md:grid-cols-3 gap-6">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.12}>
              <div data-testid={`pillar-card-${i}`} className="group h-full bg-brand-card border border-brand-ink/10 rounded-sm overflow-hidden hover:border-brand-orange transition-colors duration-500">
                <div className="relative h-44 overflow-hidden">
                  <img src={p.img} alt={p.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-brand-dark/20" />
                  <span className="absolute top-4 left-4 text-xs font-mono tracking-widest text-brand-offwhite bg-brand-dark/60 px-2 py-1 rounded-sm">{p.tag}</span>
                </div>
                <div className="p-7">
                  <p.icon className="w-8 h-8 text-brand-orange mb-4" strokeWidth={1.5} />
                  <h3 className="font-display font-semibold text-2xl tracking-tight text-brand-ink">{p.title}</h3>
                  <p className="mt-3 text-brand-grey leading-relaxed text-[15px]">{p.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const MOMENTUM = [
  { icon: TrendingUp, end: 12000, suffix: " km", label: "National highways added every year across India" },
  { icon: Building2, end: 220, suffix: "+", label: "Airports & airstrips targeted by 2030" },
  { icon: TrainFront, end: 1300, suffix: "+", label: "New & upgraded rail stations in the pipeline" },
];

function Momentum() {
  return (
    <section className="bg-brand-dark py-24 lg:py-32 relative overflow-hidden">
      <div className="grain" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <Reveal>
          <span className="text-brand-orange text-sm font-semibold tracking-[0.2em] uppercase">Chapter 02 · Current Growth Potential</span>
          <h2 className="mt-4 font-display font-semibold text-4xl sm:text-5xl tracking-tighter text-brand-offwhite max-w-3xl leading-[1.05]">India is building at a pace the map can't keep up with.</h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-px mt-16 bg-brand-offwhite/10 border border-brand-offwhite/10">
          {MOMENTUM.map((m, i) => (
            <Reveal key={i} delay={i * 0.12} className="bg-brand-dark p-10">
              <m.icon className="w-7 h-7 text-brand-orange mb-6" strokeWidth={1.5} />
              <div className="font-display font-semibold text-5xl lg:text-6xl tracking-tighter text-brand-offwhite"><CountUp end={m.end} suffix={m.suffix} /></div>
              <p className="mt-4 text-brand-offwhite/60 leading-relaxed text-[15px] max-w-xs">{m.label}</p>
            </Reveal>
          ))}
        </div>
        <p className="mt-6 text-xs text-brand-offwhite/40">Illustrative figures reflecting India's infrastructure build-out momentum.</p>
      </div>
    </section>
  );
}

function Featured() {
  return (
    <section id="featured" className="bg-brand-paper py-16 border-y border-brand-ink/10">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <p className="text-center text-xs tracking-[0.25em] uppercase text-brand-grey mb-10">Trusted signal · As featured on</p>
        <div className="flex flex-wrap items-center justify-center gap-x-16 gap-y-8">
          <img src={ASSETS.toi} alt="The Times of India" loading="lazy" className="h-8 lg:h-10 object-contain opacity-60 hover:opacity-100 transition-opacity" />
          <img src={ASSETS.deccan} alt="Deccan Herald" loading="lazy" className="h-9 lg:h-11 object-contain opacity-60 hover:opacity-100 transition-opacity" />
        </div>
        <div className="text-center mt-10">
          <Link to="/press" data-testid="featured-see-all" className="inline-flex items-center gap-2 text-brand-ink font-medium hover:text-brand-orange transition-colors underline underline-offset-4">
            Read our press coverage <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

const STATS = [
  { end: 24, suffix: "", label: "Deals closed", note: "Sourced, diligenced and exited" },
  { end: 92, prefix: "₹", suffix: " Cr", label: "Capital deployed", note: "Into registered land parcels" },
  { end: 48, suffix: "%", label: "Average annual return", note: "Historical average, not a projection" },
];

function TrackRecord() {
  return (
    <section id="track" className="bg-brand-offwhite py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <span className="text-brand-orange text-sm font-semibold tracking-[0.2em] uppercase">Chapter 03 · The Proof</span>
              <h2 className="mt-4 font-display font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-brand-ink leading-[1.05]">Our numbers speak for themselves.</h2>
              <p className="mt-6 text-lg text-brand-grey leading-relaxed">Every figure below is a realized, historical result aggregated across the deals we have sourced, closed and exited. Not a model, not a projection.</p>
              <div className="mt-8 flex items-start gap-3 bg-brand-card border border-brand-ink/10 rounded-sm p-5">
                <ShieldCheck className="w-5 h-5 text-brand-orange shrink-0 mt-0.5" />
                <p className="text-sm text-brand-grey leading-relaxed">Each transaction is independently checkable at the government sub-registrar's office. We'll walk you through exactly how to verify it.</p>
              </div>
              <p className="mt-5 text-sm text-brand-grey leading-relaxed"><span className="text-brand-ink font-medium">You own the land, never a pool.</span> Every investor takes registered title in their own name. Your money is never pooled with anyone else's.</p>
            </Reveal>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-3 gap-px bg-brand-ink/10 border border-brand-ink/10">
            {STATS.map((s, i) => (
              <Reveal key={i} delay={i * 0.12} className="bg-brand-offwhite p-8">
                <div className="font-display font-semibold text-5xl lg:text-6xl tracking-tighter text-brand-ink"><CountUp end={s.end} prefix={s.prefix || ""} suffix={s.suffix} /></div>
                <p className="mt-3 font-display font-medium text-lg text-brand-ink">{s.label}</p>
                <p className="mt-1 text-sm text-brand-grey">{s.note}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Founder() {
  return (
    <section className="bg-brand-dark py-28 lg:py-36 relative overflow-hidden">
      <div className="grain" />
      <div className="max-w-5xl mx-auto px-6 lg:px-8 relative z-10">
        <Reveal>
          <span className="text-brand-orange text-sm font-semibold tracking-[0.2em] uppercase">The BWG Promise</span>
          <Quote className="w-12 h-12 text-brand-orange mb-8 mt-4" />
          <blockquote className="font-display font-medium text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.15] text-brand-offwhite">
            "The premium on land isn't created when the road opens. It's created the moment the road becomes<span className="text-brand-orange"> inevitable</span>. Our entire job is to find that moment: early, and with proof."
          </blockquote>
          <div className="mt-10 flex items-center gap-4">
            <img src={ASSETS.founderSahil} alt="Sahil Marshall, Founder and CEO of Bhaarat Wealth Group" loading="lazy"
              className="w-14 h-14 rounded-full object-cover border border-brand-orange/40" />
            <div>
              <p className="font-semibold text-brand-offwhite">Sahil Marshall</p>
              <p className="text-sm text-brand-offwhite/50">Founder & CEO, Bhaarat Wealth Group</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

const CLU_STEPS = [
  { key: "agri", label: "Agricultural", tier: "Entry", desc: "Land acquired at agricultural value along a sanctioned corridor, where the infrastructure case is already visible in tender data." },
  { key: "pending", label: "CLU-Pending", tier: "Re-rating", desc: "The legal Change-of-Land-Use process begins. As eligibility firms up, the market starts pricing in the permitted future use." },
  { key: "granted", label: "CLU-Granted", tier: "Realized", desc: "With commercial or residential use legally permitted, the parcel reaches its re-rated value, and the exit window opens." },
];

function SuccessStory() {
  const [active, setActive] = useState(0);
  return (
    <section className="bg-brand-paper py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative rounded-sm overflow-hidden border border-brand-ink/10">
              <img src={ASSETS.success} alt="Aerial view of an emerging Indian development corridor" loading="lazy" className="w-full h-[420px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/60 to-transparent" />
              <div className="absolute bottom-6 left-6">
                <p className="text-xs tracking-[0.2em] uppercase text-brand-offwhite/70">How we work</p>
                <p className="font-display font-semibold text-2xl text-brand-offwhite">From farmland to re-rated asset</p>
              </div>
            </div>
          </Reveal>
          <div>
            <span className="text-brand-orange text-sm font-semibold tracking-[0.2em] uppercase">Chapter 04 · The Path</span>
            <h2 className="mt-4 font-display font-semibold text-4xl sm:text-5xl tracking-tighter text-brand-ink leading-[1.05]">How a parcel's value actually jumps.</h2>
            <p className="mt-5 text-brand-grey leading-relaxed">The clearest upside in Indian land isn't luck. It's a legal transition. Watch the step-up:</p>
            <div data-testid="clu-diagram" className="mt-8 space-y-3">
              {CLU_STEPS.map((s, i) => (
                <button key={s.key} data-testid={`clu-step-${i}`} onClick={() => setActive(i)}
                  className={`w-full text-left border rounded-sm p-5 transition-all duration-300 relative overflow-hidden ${active === i ? "border-brand-orange bg-brand-card" : "border-brand-ink/10 bg-transparent hover:border-brand-ink/30"}`}>
                  {active === i && <motion.span layoutId="clu-active" className="absolute left-0 top-0 bottom-0 w-1 bg-brand-orange" />}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className="font-mono text-sm text-brand-grey">0{i + 1}</span>
                      <span className="font-display font-semibold text-xl text-brand-ink">{s.label}</span>
                    </div>
                    <span className={`text-xs font-semibold px-3 py-1 rounded-full ${active === i ? "bg-brand-orange text-white" : "bg-brand-ink/5 text-brand-grey"}`}>{s.tier}</span>
                  </div>
                  <AnimatePresence>
                    {active === i && (
                      <motion.p initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="text-brand-grey text-[15px] leading-relaxed mt-3 overflow-hidden">{s.desc}</motion.p>
                    )}
                  </AnimatePresence>
                </button>
              ))}
            </div>
            <p className="mt-5 text-xs text-brand-grey">Illustrative of BWG's approach. Every parcel is diligenced individually; outcomes vary.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Investors() {
  const { open: openBooking } = useBooking();
  return (
    <section className="bg-brand-offwhite py-24 lg:py-28">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <Reveal>
          <div className="bg-brand-card border border-brand-ink/10 rounded-sm p-10 lg:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="max-w-2xl">
              <h2 className="font-display font-semibold text-3xl sm:text-4xl tracking-tighter text-brand-ink leading-tight">Talk to our investors, before you talk to us.</h2>
              <p className="mt-4 text-brand-grey leading-relaxed">We'll connect you with people who've already taken title to a parcel through BWG. Ask them anything: the diligence, the paperwork, the exit. Then decide.</p>
            </div>
            <button type="button" onClick={() => openBooking("default", "investors_modal")} data-testid="investors-cta-button" className="group shrink-0 inline-flex items-center gap-2 rounded-full border border-brand-ink px-7 py-4 font-semibold text-brand-ink hover:bg-brand-ink hover:text-brand-offwhite transition-colors duration-300">
              Request a reference call <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

const SLOTS = ["11:00 AM", "3:30 PM", "6:00 PM"];
export function LeadCapture() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [slot, setSlot] = useState(SLOTS[0]);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) { toast.error("Please fill in your name, phone and email."); return; }
    setLoading(true);
    // Static site: the only destination is Zoho CRM Web-to-Lead
    // (fire-and-forget hidden-iframe POST). Success UX is driven by
    // client-side validation passing.
    postToZohoWebToLead({ name: form.name, phone: form.phone, email: form.email, message: form.message, source: "homepage_inline", slot });
    toast.success("Thank you! A Wealth-Maker will call you shortly.");
    setForm({ name: "", phone: "", email: "", message: "" });
    setLoading(false);
  };
  const field = "w-full bg-transparent border-b border-brand-offwhite/25 py-3 text-brand-offwhite placeholder:text-brand-offwhite/40 focus:border-brand-orange focus:outline-none transition-colors";
  return (
    <section id="book" className="bg-brand-dark py-24 lg:py-32 relative overflow-hidden">
      <div className="grain" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <span className="text-brand-orange text-sm font-semibold tracking-[0.2em] uppercase">Book your call</span>
            <h2 className="mt-4 font-display font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-brand-offwhite leading-[1.02]">Book a free 15-minute call with a Wealth-Maker.</h2>
            <p className="mt-6 text-lg text-brand-offwhite/60 leading-relaxed max-w-lg">No pressure, no pooled fund pitch. A focused conversation about corridors, parcels and how we verify everything.</p>
            <div className="mt-10">
              <p className="text-xs tracking-[0.2em] uppercase text-brand-offwhite/50 mb-4">Today's available slots</p>
              <div className="flex flex-wrap gap-3">
                {SLOTS.map((s) => (
                  <button key={s} type="button" data-testid={`slot-${s.replace(/[:\s]/g, "")}`} onClick={() => setSlot(s)}
                    className={`px-5 py-3 rounded-sm border text-sm font-medium transition-all duration-300 ${slot === s ? "bg-brand-orange border-brand-orange text-white" : "border-brand-offwhite/20 text-brand-offwhite/70 hover:border-brand-offwhite/50"}`}>{s}</button>
                ))}
              </div>
            </div>
          </div>
          <form onSubmit={submit} data-testid="lead-form" className="space-y-7">
            <input data-testid="lead-name" className={field} placeholder="Full name" value={form.name} onChange={set("name")} />
            <input data-testid="lead-phone" className={field} placeholder="Phone number" value={form.phone} onChange={set("phone")} />
            <input data-testid="lead-email" type="email" className={field} placeholder="Email address" value={form.email} onChange={set("email")} />
            <textarea data-testid="lead-message" rows={3} className={field + " resize-none"} placeholder="Anything we should know? (optional)" value={form.message} onChange={set("message")} />
            <button type="submit" data-testid="lead-submit" disabled={loading} className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange px-8 py-4 font-semibold text-white hover:bg-brand-offwhite hover:text-brand-ink transition-colors duration-300 disabled:opacity-60">
              {loading ? "Sending…" : "Request my call"}<ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </button>
            <p className="text-xs text-brand-offwhite/40">By submitting you agree to be contacted by BWG. We never share your details.</p>
          </form>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <main className="bg-brand-paper overflow-x-hidden">
      <Seo
        title="Bhaarat Wealth Group | Land Investment Advisory"
        description="BWG identifies high-conviction land corridors before infrastructure premiums price in. 24 deals, ₹92 Cr deployed, 48% average return. Book a free 15-minute call."
        path="/"
        schema={[orgSchema(), faqSchema(FAQS), breadcrumbSchema([{ name: "Home", path: "/" }])]}
      />
      <Nav overHero />
      <Hero />
      <Transition />
      <Intelligence />
      <RociEngine />
      <Momentum />
      <Featured />
      <TrackRecord />
      <Founder />
      <SuccessStory />
      <WhereWeOperate />
      <Investors />
      <LeadCapture />
      <FaqSection />
      <Footer />
      <WhatsAppFab />
    </main>
  );
}
